// ==================================================================================
// Digital Approval System (DAS) - Attachments Hook
// ==================================================================================
// Custom hook for attachment operations
// ==================================================================================

import { useState, useEffect, useCallback } from 'react';
import { attachmentsService } from '../../../api/services/das/attachmentsService';
import type {
  Attachment,
  UploadAttachmentRequest,
  AttachmentStats,
  FileUploadState,
  FileSizeValidationResult,
  FileSizeLimits,
  FileUploadSettings,
} from '../../../api/types/attachments.types';
import {
  validateFileSize,
  calculateTotalFileSize,
  formatFileSize,
  toFileSizeLimits,
  DEFAULT_FILE_SIZE_LIMITS,
  MAX_INDIVIDUAL_FILE_SIZE,
  MAX_TOTAL_ATTACHMENTS_SIZE,
} from '../../../api/types/attachments.types';
import type { ID } from '../../../api/types';

/**
 * Hook for fetching attachments by request ID
 */
export function useAttachmentsByTicketId(requestId: ID | null) {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAttachments = useCallback(async () => {
    if (!requestId) {
      setAttachments([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await attachmentsService.getByTicketId(requestId);
      setAttachments(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch attachments');
      console.error('Failed to fetch attachments:', err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  return {
    attachments,
    loading,
    error,
    refetch: fetchAttachments,
  };
}

/**
 * Hook for uploading files with progress tracking and size validation
 */
export function useFileUpload(customLimits?: FileSizeLimits) {
  const [uploadStates, setUploadStates] = useState<Map<string, FileUploadState>>(
    new Map()
  );

  const limits = customLimits || DEFAULT_FILE_SIZE_LIMITS;

  /**
   * Validates a file against size limits
   * @param file - The file to validate
   * @param existingFiles - Array of already added files to calculate total size
   * @returns FileSizeValidationResult with validation status and formatted sizes
   */
  const validateFile = useCallback(
    (file: File, existingFiles: File[] = []): FileSizeValidationResult => {
      const currentTotalSize = calculateTotalFileSize(existingFiles);
      return validateFileSize(file, currentTotalSize, limits);
    },
    [limits]
  );

  /**
   * Calculates the total size of files
   * @param files - Array of files
   * @returns Total size in bytes
   */
  const getTotalSize = useCallback((files: File[]): number => {
    return calculateTotalFileSize(files);
  }, []);

  /**
   * Gets formatted total size of files
   * @param files - Array of files
   * @returns Formatted total size string
   */
  const getFormattedTotalSize = useCallback((files: File[]): string => {
    return formatFileSize(calculateTotalFileSize(files));
  }, []);

  const uploadFile = useCallback(
    async (
      file: File,
      request: UploadAttachmentRequest
    ): Promise<Attachment | null> => {
      const fileKey = `${file.name}_${Date.now()}`;

      // Initialize upload state
      setUploadStates((prev) => {
        const newMap = new Map(prev);
        newMap.set(fileKey, {
          file,
          documentTitle: request.documentTitle,
          documentDescription: request.documentDescription,
          progress: 0,
          status: 'uploading',
        });
        return newMap;
      });

      try {
        const attachment = await attachmentsService.upload(
          file,
          request,
          (progressEvent: any) => {
            if (progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );

              setUploadStates((prev) => {
                const newMap = new Map(prev);
                const state = newMap.get(fileKey);
                if (state) {
                  newMap.set(fileKey, { ...state, progress });
                }
                return newMap;
              });
            }
          }
        );

        // Update state to success
        setUploadStates((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(fileKey);
          if (state && attachment) {
            newMap.set(fileKey, {
              ...state,
              status: 'success',
              progress: 100,
              attachmentId: attachment.id,
            });
          }
          return newMap;
        });

        return attachment;
      } catch (err) {
        const error = err as Error;
        console.error('File upload failed:', err);

        // Update state to error
        setUploadStates((prev) => {
          const newMap = new Map(prev);
          const state = newMap.get(fileKey);
          if (state) {
            newMap.set(fileKey, {
              ...state,
              status: 'error',
              error: error.message || 'Upload failed',
            });
          }
          return newMap;
        });

        // Re-throw so Promise.allSettled() correctly marks this as 'rejected'
        throw err;
      }
    },
    []
  );

  const clearUploadState = useCallback((fileKey: string) => {
    setUploadStates((prev) => {
      const newMap = new Map(prev);
      newMap.delete(fileKey);
      return newMap;
    });
  }, []);

  const clearAllUploadStates = useCallback(() => {
    setUploadStates(new Map());
  }, []);

  return {
    uploadFile,
    uploadStates,
    clearUploadState,
    clearAllUploadStates,
    // File size validation utilities
    validateFile,
    getTotalSize,
    getFormattedTotalSize,
    fileSizeLimits: limits,
  };
}

// Re-export file size utilities for direct use in components
export {
  formatFileSize,
  validateFileSize,
  calculateTotalFileSize,
  MAX_INDIVIDUAL_FILE_SIZE,
  MAX_TOTAL_ATTACHMENTS_SIZE,
  DEFAULT_FILE_SIZE_LIMITS,
};
export type { FileSizeValidationResult, FileSizeLimits };

/**
 * Hook for fetching attachment statistics for a request
 */
export function useAttachmentStats(requestId: ID | null) {
  const [stats, setStats] = useState<AttachmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    if (!requestId) {
      setStats(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await attachmentsService.getStats(requestId);
      setStats(data);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch attachment statistics');
      console.error('Failed to fetch attachment statistics:', err);
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
}

/**
 * Hook for managing attachment operations (delete, update)
 */
export function useAttachmentOperations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAttachment = useCallback(async (id: ID): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      await attachmentsService.delete(id);
      return true;
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to delete attachment');
      console.error('Failed to delete attachment:', err);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadAttachment = useCallback(
    async (id: ID, fileName: string): Promise<boolean> => {
      try {
        setLoading(true);
        setError(null);
        await attachmentsService.download(id, fileName);
        return true;
      } catch (err) {
        const error = err as Error;
        setError(error.message || 'Failed to download attachment');
        console.error('Failed to download attachment:', err);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    deleteAttachment,
    downloadAttachment,
    loading,
    error,
  };
}

/**
 * Hook for fetching file upload settings from API
 * Returns settings from API or falls back to default limits if API unavailable
 */
export function useFileUploadSettings() {
  const [settings, setSettings] = useState<FileUploadSettings | null>(null);
  const [limits, setLimits] = useState<FileSizeLimits>(DEFAULT_FILE_SIZE_LIMITS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await attachmentsService.getSettings();
      setSettings(data);
      setLimits(toFileSizeLimits(data));
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Failed to fetch file upload settings');
      console.warn('Failed to fetch file upload settings, using defaults:', err);
      // Keep using default limits on error
      setLimits(DEFAULT_FILE_SIZE_LIMITS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    limits,
    loading,
    error,
    refetch: fetchSettings,
  };
}
